import { useState, type FormEvent } from "react";
import { api, ApiError } from "./api.ts";
import { Label } from "@/components/ui/label.tsx";
import { Alert, AlertDescription } from "@/components/ui/alert.tsx";
import { DelegateLockup } from "@/components/brand/delegate-mark.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card.tsx";

/**
 * Shown instead of the app while `passwordResetRequired` is true.
 *
 * The current password is asked for again even though the user just signed in
 * with it: the server requires it, so that a stolen session alone cannot be
 * used to set a password the real owner does not know.
 */
export function ChangePassword({ onDone }: { onDone: () => void }) {
  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      setError("new passwords do not match");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api("/api/auth/change-password", {
        body: { currentPassword: current, newPassword: password },
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "could not change password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="grid min-h-dvh place-items-center bg-background p-6">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <DelegateLockup className="mb-2" />
          <CardTitle>Set your password</CardTitle>
          <CardDescription>
            This account was created with a one-time password. Enter the one-time password you
            just signed in with, then choose a password of your own.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="flex flex-col gap-4">
            <div className="grid gap-2">
              <Label htmlFor="current-password">One-time password</Label>
            <Input
              id="current-password"
              type="password"
              autoComplete="current-password"
              placeholder="one-time password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
            />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password-password">New password</Label>
            <Input
              id="password-password"
              type="password"
              autoComplete="new-password"
              placeholder="new password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={8}
              required
            />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="confirm-password">Confirm new password</Label>
            <Input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              placeholder="confirm new password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              minLength={8}
              required
            />
            </div>
            {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
            <Button type="submit" disabled={busy} className="w-full">
              {busy ? "Saving..." : "Set password"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </main>
  );
}
